/**
 * Per-site settings read at run time.
 *
 * Vite inlines VITE_* values at build time, so anything a site must be able to
 * change after the image is built cannot live there. The image's entrypoint
 * writes /site-config.js from the container's environment instead, index.html
 * loads it before the bundle, and this module is the only reader. The VITE_*
 * values remain as build-time defaults for deployments that set nothing.
 */

export type ProductId = 'synstream-enterprise' | 'pipeline-studio' | 'auto-monitor'

export const ALL_PRODUCTS: ProductId[] = ['synstream-enterprise', 'pipeline-studio', 'auto-monitor']

interface SiteConfig {
  /** Products to show; an array or a comma-separated string. Empty means all. */
  products?: string[] | string
  /** Dashboard address when it is not dashboard.<domain> of this console. */
  dashboardUrl?: string
}

declare global {
  interface Window {
    __SYNSTREAM_SITE__?: SiteConfig
  }
}

function siteConfig(): SiteConfig {
  if (typeof window === 'undefined') return {}
  const cfg = window.__SYNSTREAM_SITE__
  return cfg && typeof cfg === 'object' ? cfg : {}
}

function toList(value: string[] | string | undefined): string[] {
  const parts = Array.isArray(value) ? value : (value || '').split(',')
  return parts.map((p) => String(p).trim().toLowerCase()).filter(Boolean)
}

/** Dashboard address set for this site, or '' when the convention applies. */
export function siteDashboardUrl(): string {
  return (siteConfig().dashboardUrl || '').trim()
}

/**
 * Products this site shows, or null when it shows every product.
 *
 * The run-time list wins over the build-time one. Ids that are not products
 * are ignored, so a typo hides nothing by accident; a list made only of typos
 * is treated as empty, which shows everything.
 */
export function visibleProducts(): ProductId[] | null {
  const runtime = toList(siteConfig().products)
  const chosen = runtime.length ? runtime : toList(import.meta.env.VITE_PRODUCTS as string | undefined)
  const known = ALL_PRODUCTS.filter((id) => chosen.includes(id))
  return known.length ? known : null
}

export function isProductVisible(productId: ProductId): boolean {
  const allowed = visibleProducts()
  return allowed === null || allowed.includes(productId)
}

/** Same check for callers holding a plain id; ids that are not products are always visible. */
export function isProductIdVisible(id: string): boolean {
  const allowed = visibleProducts()
  return allowed === null || !ALL_PRODUCTS.includes(id as ProductId) || allowed.includes(id as ProductId)
}
